import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { ApiError } from '@shared/api/errors';
import {
  HelpdeskConversationView,
  HelpdeskRequestForm,
  type RequesterHelpdeskRuntime,
} from '@features/helpdesk/embed';
import {
  Button,
  Heading,
  PaginationControls,
  Spinner,
  StatusBadge,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
} from '@shared/ui';
import { HELPDESK_PRIVACY_POLICY_URL, type HelpdeskWidgetConfiguration } from '../model/config';
import { WidgetSession, type WidgetSessionSnapshot } from '../model/widget-session';
import { BubbleSession, type BubbleSnapshot } from '../model/bubble-session';

class GuestStorageUnavailable extends Error {}

/** Requester workflow shared by the popup and the standalone hosted page. */
export function HelpdeskContent({
  configuration,
  runtime,
  configurationError,
  headingId,
  headingLevel = 'h2',
  onClose,
  session,
  sessionSnapshot,
  bubble,
  bubbleSnapshot,
  onConnect,
  connectionBusy = false,
  connectionError = null,
}: {
  configuration: HelpdeskWidgetConfiguration;
  runtime: RequesterHelpdeskRuntime;
  configurationError?: string;
  headingId: string;
  headingLevel?: 'h1' | 'h2';
  onClose?: () => void;
  session?: WidgetSession | null;
  sessionSnapshot?: WidgetSessionSnapshot;
  bubble?: BubbleSession | null;
  bubbleSnapshot?: BubbleSnapshot;
  onConnect?: () => Promise<void>;
  connectionBusy?: boolean;
  connectionError?: string | null;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [tab, setTab] = useState('new');
  const [taskId, setTaskId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const bubbleActor = !session?.hasAccountActor() && bubble?.hasAccountActor() === true;
  const actorStatus = session?.hasAccountActor()
    ? sessionSnapshot?.status
    : bubbleActor
      ? bubbleSnapshot?.status
      : bubbleSnapshot?.status === 'resolving' ? 'resolving' : 'guest';
  const identityResolving = actorStatus === 'resolving';
  const logout = async () => {
    if (!session) return;
    await session.logout();
  };
  const bootstrap = useQuery({
    queryKey: ['helpdesk-widget', runtime.scope, 'bootstrap'],
    queryFn: async ({ signal }) => {
      const response = await runtime.bootstrap({ signal });
      if (response.data.requester.mode === 'guest') {
        try {
          await runtime.bootstrap({ verifyGuestCookie: true, signal });
        } catch (error) {
          if (signal.aborted) throw error;
          if (error instanceof ApiError && error.status === 401)
            throw new GuestStorageUnavailable('Guest storage is unavailable.');
          throw error;
        }
      }
      return response;
    },
    enabled: configurationError === undefined && !identityResolving,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const history = useQuery({
    queryKey: ['helpdesk-widget', runtime.scope, 'list', page],
    queryFn: ({ signal }) => runtime.list({ page, pageSize: 10, signal }),
    enabled: tab === 'list' && bootstrap.isSuccess && !bootstrap.isFetching && !identityResolving,
    retry: false,
    staleTime: 0,
  });
  const totalHistoryPages = history.data?.meta.totalPages;
  useEffect(() => {
    if (totalHistoryPages !== undefined && !history.isFetching && !history.isError) {
      setPage((current) => Math.min(current, Math.max(1, totalHistoryPages)));
    }
  }, [totalHistoryPages, history.isFetching, history.isError]);

  const reset = () => {
    setTaskId(null);
    setPage(1);
    setTab('new');
  };
  const failed = configurationError !== undefined || bootstrap.isError;
  return (
    <>
      <header className="relative shrink-0 px-4 pb-5 pt-4 sm:px-8 sm:pb-6 sm:pt-8">
        <Heading
          as={headingLevel}
          size="3xl"
          weight="bold"
          id={headingId}
          className="pr-9 text-[24px] leading-8 sm:text-[32px] sm:leading-10"
        >
          {t('helpdesk.widget.heading', "Need help? We're here for you!")}
        </Heading>
        {onClose ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1 sm:right-2 sm:top-2"
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
          >
            <X aria-hidden="true" className="h-6 w-6" />
          </Button>
        ) : null}
      </header>
      {session ? <div className="shrink-0 space-y-2 px-4 pb-3 sm:px-8">
        {actorStatus === 'restoring' || actorStatus === 'resolving' ? <Text role="status">
          {t('helpdesk.widget.restoring', 'Restoring your Move Helpdesk connection…')}
        </Text> : actorStatus === 'connected' ? <div className="flex flex-wrap items-center justify-between gap-2">
          <Text size="sm">{bootstrap.data?.data.requester.mode === 'account' && bootstrap.data.data.requester.email
            ? t('helpdesk.widget.connectedAs', 'Connected as {{email}}', { email: bootstrap.data.data.requester.email })
            : bubbleActor ? t('helpdesk.widget.connectedHelpdesk', 'Connected to Helpdesk')
              : t('helpdesk.widget.connectedMove', 'Connected to Move')}</Text>
          <div className="flex flex-wrap gap-2">
            {bubbleActor ? <Button type="button" variant="outline" onClick={() => void onConnect?.()} disabled={connectionBusy}>
              {t('helpdesk.widget.connect', 'Sign in to Move')}
            </Button> : null}
            <Button type="button" variant="outline" onClick={() => void logout()}>
              {t('helpdesk.widget.disconnect', 'Sign out of Helpdesk')}
            </Button>
          </div>
        </div> : actorStatus === 'reconnect' ? <div role="alert" className="space-y-2">
          <Text>{t('helpdesk.widget.reconnect', 'Your Move Helpdesk connection is unavailable. Your draft is kept here. Retry or sign in again.')}</Text>
          <div className="flex flex-wrap gap-2">
            {(bubbleActor || session.canRetry()) ? <Button type="button" variant="outline" onClick={() => { if (bubbleActor) bubble.retry(); else session.retry(); }}>{t('common.retry', 'Retry')}</Button> : null}
            <Button type="button" onClick={() => void onConnect?.()} disabled={connectionBusy}>
              {t('helpdesk.widget.connect', 'Sign in to Move')}
            </Button>
          </div>
        </div> : <Button type="button" variant="outline" onClick={() => void onConnect?.()} disabled={connectionBusy}>
          {t('helpdesk.widget.connect', 'Sign in to Move')}
        </Button>}
        {connectionError ? <Text role="alert">{connectionError}</Text> : null}
        {sessionSnapshot?.notice === 'revocation-unconfirmed' ? <Text role="alert">{t('helpdesk.widget.revocationPending', 'Signed out here. Server revocation could not be confirmed while offline.')}</Text> : null}
        {sessionSnapshot?.notice === 'storage-unavailable' ? <Text role="alert">{t('helpdesk.widget.storageLogoutFailed', 'Signed out here, but this browser could not save your sign-out preference.')}</Text> : null}
      </div> : null}
      <Tabs
        value={tab}
        onValueChange={(value) => {
          setTaskId(null);
          setPage(1);
          setTab(value);
        }}
        className="shrink-0 px-4 sm:px-8"
      >
        <TabsList variant="primary" className="w-full">
          <TabsTrigger value="new" variant="primary" className="min-h-11 px-3 sm:px-5">
            {t('helpdesk.widget.newRequest', 'New request')}
          </TabsTrigger>
          <TabsTrigger value="list" variant="primary" className="min-h-11 px-3 sm:px-5">
            {t('helpdesk.widget.myRequests', 'My requests')}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-6 pt-6 sm:px-8 sm:pb-8">
        {identityResolving ? <Spinner aria-label={t('common.loading', 'Loading...')} /> : failed ? (
          <div role="alert" className="space-y-3">
            <Text>
              {configurationError !== undefined
                ? t(
                    'helpdesk.widget.configurationError',
                    'The support widget is not configured correctly.'
                  )
                : bootstrap.error instanceof ApiError &&
                    bootstrap.error.code === 'HELPDESK_INVALID_SOURCE'
                  ? t(
                      'helpdesk.widget.sourceUnavailable',
                      'The configured support source is unavailable. Contact the website administrator.'
                    )
                  : bootstrap.error instanceof GuestStorageUnavailable
                    ? t(
                        'helpdesk.widget.storageBlocked',
                        'Your browser cannot save this support session. Open support in Move to continue.'
                      )
                    : t(
                        'helpdesk.widget.loadFailed',
                        'Support could not be loaded. Please try again.'
                      )}
            </Text>
            {configurationError === undefined ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => void bootstrap.refetch()}
                disabled={bootstrap.isFetching}
              >
                {t('common.retry', 'Retry')}
              </Button>
            ) : null}
            <a
              href={configuration.supportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block min-h-11 text-primary underline"
            >
              {t('helpdesk.widget.openMove', 'Open support in Move')}
            </a>
          </div>
        ) : bootstrap.isPending ? (
          <Spinner aria-label={t('common.loading', 'Loading...')} />
        ) : taskId !== null ? (
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setTaskId(null);
                setTab('list');
                void client.invalidateQueries({
                  queryKey: ['helpdesk-widget', runtime.scope, 'list'],
                });
              }}
            >
              {t('helpdesk.widget.myRequests', 'My requests')}
            </Button>
            <HelpdeskConversationView
              taskUuid={taskId}
              viewerAccessScope={runtime.scope}
              canCreate
              active
            />
          </div>
        ) : tab === 'new' ? (
          <HelpdeskRequestForm
            presentation="widget"
            options={bootstrap.data.data}
            privacyPolicyUrl={HELPDESK_PRIVACY_POLICY_URL}
            onCreated={(id) => {
              setTab('list');
              setTaskId(id);
              void client.invalidateQueries({
                queryKey: ['helpdesk-widget', runtime.scope, 'list'],
              });
            }}
            onCancel={onClose ?? reset}
          />
        ) : history.isError ? (
          <div role="alert" className="space-y-3">
            <Text>
              {t('helpdesk.widget.loadFailed', 'Support could not be loaded. Please try again.')}
            </Text>
            <Button type="button" variant="outline" onClick={() => void history.refetch()}>
              {t('common.retry', 'Retry')}
            </Button>
          </div>
        ) : history.isPending ? (
          <Spinner aria-label={t('common.loading', 'Loading...')} />
        ) : (
          <div className="space-y-3">
            {history.data.items.length === 0 ? (
              <Text>{t('helpdesk.widget.empty', 'You have no support requests yet.')}</Text>
            ) : (
              <ul className="space-y-2">
                {history.data.items.map((task) => (
                  <li key={task.id}>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-auto min-h-11 w-full justify-between gap-3 whitespace-normal text-left"
                      onClick={() => {
                        setTaskId(task.id);
                      }}
                    >
                      <span className="min-w-0 space-y-1 break-words">
                        <span className="block">
                          {task.title ??
                            task.taskId ??
                            t('helpdesk.widget.request', 'Support request')}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {task.createdAt === null ? (
                            '—'
                          ) : (
                            <time dateTime={task.createdAt}>
                              {new Intl.DateTimeFormat(configuration.locale, {
                                dateStyle: 'medium',
                              }).format(new Date(task.createdAt))}
                            </time>
                          )}
                        </span>
                      </span>
                      <StatusBadge tone={task.isClosed ? 'positive' : 'informational'} size="sm">
                        {task.isClosed
                          ? t('helpdesk.widget.completed', 'Completed')
                          : task.status
                            ? t(`helpdesk.status.${task.status.dbValue}`, task.status.display)
                            : t('helpdesk.widget.active', 'Active')}
                      </StatusBadge>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {page > 1 || history.data.meta.totalPages > 1 ? (
              <nav aria-label={t('helpdesk.widget.myRequests', 'My requests')}>
                <PaginationControls
                  size="adaptive"
                  totalItems={history.data.meta.totalItems}
                  pageSize={10}
                  currentPage={page}
                  onPageChange={setPage}
                  disabled={history.isFetching}
                  labels={{
                    showing: (from, to, total) =>
                      t('common.pagination.showing', { from, to, total }),
                    page: t('common.pagination.page'),
                    of: (count) => t('common.pagination.of', { count }),
                    previous: t('common.previous', 'Previous'),
                    next: t('common.next', 'Next'),
                  }}
                />
              </nav>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
}
